var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/** @jsx jsx */
/** @jsxFrag */
import { React, ReactRedux, css, hooks, jsx, AppMode, ECookieBannerLayout, utils, Immutable, FixedPosition, moduleLoader, getAppStore, appActions, getNextAnimationId, AnimationComponent, jimuHistory, ReactResizeDetector, BrowserSizeMode, lodash } from 'jimu-core';
import defaultMessages from '../../translations/default';
import { Button, Icon, ModalOverlayIdContext, RichTextDisplayer, styleUtils, useOverlayManager } from 'jimu-ui';
import CookieSettingsWindow from './cookie-settings-window';
import { bindResizeHandler } from './banner-resize-utils';
import { OverlayPortal } from '../overlay/overlay-portal';
const getWrapperStyle = (zIndex) => {
    return css `
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    z-index: ${zIndex};
  `;
};
const getMaskStyle = () => {
    return css `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  `;
};
// Styles for resize handlers
export function getResizeStyle() {
    const length = 38;
    return css `
    .cookie-banner-resize-handlers{
      .resize-handler{
        width: 100%;
        height: 100%;
        position: absolute;
        .resize-handler-icon {
          position: absolute;
          background-color: var(--ref-palette-black);
          border: 1px solid var(--ref-palette-white);
        }
        &.cookie-banner-resize-top, &.cookie-banner-resize-bottom {
          height: 4px;
          cursor: ns-resize;
          .resize-handler-icon{
            left: min(50%, 50vw);
            width: ${length}px;
            margin-left: ${-length / 2}px;
          }
        }
        &.cookie-banner-resize-top{
          top: 0;
        }
        &.cookie-banner-resize-bottom{
          bottom: 0;
        }
        &.cookie-banner-resize-left, &.cookie-banner-resize-right{
          width: 4px;
          top: 0;
          cursor: ew-resize;
          .resize-handler-icon{
            top: 50%;
            height: ${length}px;
            margin-top: ${-length / 2}px;
          }
        }
        &.cookie-banner-resize-left{
          left: 0;
        }
        &.cookie-banner-resize-right{
          right: 0;
        }
      }
    }
  `;
}
const calculateDaysBetween = (endTimeStamp, startTimeStamp) => {
    const SECONDS_PER_DAY = 1000 * 60 * 60 * 24;
    return Math.round(Math.abs(endTimeStamp - startTimeStamp) / SECONDS_PER_DAY);
};
export const DEFAULT_FIXED_POSITION_SIZE_JSON = Immutable({
    position: FixedPosition.BottomRight,
    height: '230px',
    heightMode: 'CUSTOM',
    offsetX: -10,
    offsetY: -10,
    width: '300px',
    widthMode: 'CUSTOM'
});
export const CookieBanner = function (props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    const { theme } = props;
    const translate = hooks.useTranslation(defaultMessages);
    const [RichEditorEntry, setRichEditorEntry] = React.useState(null);
    const [ThemeEntry, setThemeEntry] = React.useState(null);
    const [BuilderEntry, setBuilderEntry] = React.useState(null);
    const [LayoutsEntry, setLayoutsEntry] = React.useState(null);
    const [DndEntry, setDndEntry] = React.useState(null);
    const [isBannerOpen, setIsBannerOpen] = React.useState(false);
    const cookieBannerConfig = ReactRedux.useSelector((state) => { var _a; return (_a = state === null || state === void 0 ? void 0 : state.appConfig) === null || _a === void 0 ? void 0 : _a.cookieBanner; });
    const browserSizeMode = ReactRedux.useSelector((state) => state.browserSizeMode);
    const [positionSizeJson, setPositionSizeJson] = React.useState(((_a = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.positionSize) === null || _a === void 0 ? void 0 : _a[browserSizeMode]) || ((_b = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.positionSize) === null || _b === void 0 ? void 0 : _b[BrowserSizeMode.Large]) || DEFAULT_FIXED_POSITION_SIZE_JSON);
    const isCookieBannerOpenByPrivacyPanel = (_c = ReactRedux.useSelector((state) => { var _a, _b; return (_b = (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.cookieBanner) === null || _b === void 0 ? void 0 : _b.isCookieBannerOpenByPrivacyPanel; })) !== null && _c !== void 0 ? _c : false;
    const isCookieBannerOpenByUrl = (_d = ReactRedux.useSelector((state) => { var _a, _b; return (_b = (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.cookieBanner) === null || _b === void 0 ? void 0 : _b.isCookieBannerOpenByUrl; })) !== null && _d !== void 0 ? _d : false;
    const enableTracking = (_e = ReactRedux.useSelector((state) => { var _a, _b; return (_b = (_a = state === null || state === void 0 ? void 0 : state.appConfig) === null || _a === void 0 ? void 0 : _a.analytics) === null || _b === void 0 ? void 0 : _b.enable; })) !== null && _e !== void 0 ? _e : false;
    const appMode = ReactRedux.useSelector((state) => { var _a; return (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.appMode; });
    const currentDialogId = ReactRedux.useSelector((state) => { var _a; return (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.currentDialogId; });
    const [isEditable, setIsEditable] = React.useState(true);
    const [playId, setPlayId] = React.useState(0);
    const previewDialogId = ReactRedux.useSelector((state) => { var _a, _b; return (_b = (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.animationPreview) === null || _b === void 0 ? void 0 : _b.dialogId; });
    const [openSettingsWindow, setOpenSettingsWindow] = React.useState(false);
    const isCookieSettingsWindowOpen = ReactRedux.useSelector((state) => { var _a, _b; return (_b = (_a = state === null || state === void 0 ? void 0 : state.appRuntimeInfo) === null || _a === void 0 ? void 0 : _a.cookieBanner) === null || _b === void 0 ? void 0 : _b.isCookieSettingsWindowOpen; });
    const [isAnimationPlayed, setIsAnimationPlayed] = React.useState(false);
    const [interactive, setInteractive] = React.useState(null);
    const bannerContainerRef = React.useRef(null);
    const editorRef = React.useRef(null);
    const [bannerWidth, setBannerWidth] = React.useState(bannerContainerRef.current && bannerContainerRef.current.width);
    const [isEditingText, setIsEditingText] = React.useState(false);
    const { overlayId, zIndex, openOverlay, activateOverlay } = useOverlayManager(window.jimuConfig.isInBuilder ? 'modal' : 'popper', false, false, false);
    React.useEffect(() => {
        if (!window.jimuConfig.isInBuilder) {
            return;
        }
        if (currentDialogId) {
            setTimeout(() => {
                activateOverlay();
            }, 100);
        }
    }, [currentDialogId, activateOverlay]);
    React.useEffect(() => {
        setOpenSettingsWindow(isCookieSettingsWindowOpen);
    }, [isCookieSettingsWindowOpen]);
    React.useEffect(() => {
        var _a, _b;
        setPositionSizeJson(((_a = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.positionSize) === null || _a === void 0 ? void 0 : _a[browserSizeMode]) || ((_b = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.positionSize) === null || _b === void 0 ? void 0 : _b[BrowserSizeMode.Large]) || DEFAULT_FIXED_POSITION_SIZE_JSON);
    }, [browserSizeMode, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.positionSize]);
    const acceptAllButtonConfig = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.acceptAllButton;
    const closeButtonConfig = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.closeButton;
    const declineAllButtonConfig = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.declineAllButton;
    const cookieSettingsButtonConfig = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.cookieSettingsButton;
    const displayText = (cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.displayRichText) || translate('optionalBannerDefaultText');
    const getContainerStyle = React.useCallback(() => {
        var _a;
        const transformStyle = LayoutsEntry && LayoutsEntry.controlPanelUtils.panelTransformStyle(positionSizeJson);
        const panelStyle = LayoutsEntry && LayoutsEntry.controlPanelUtils.convertToStyle(positionSizeJson);
        const borderRadiusStyle = (cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.borderRadius) ? styleUtils.toCSSBorderRadius(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.borderRadius.asMutable({ deep: true })) : '';
        const boxShadowStyle = ((cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) && !((_a = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) === null || _a === void 0 ? void 0 : _a.presetStyle)) ? styleUtils.toCSSBoxshadow(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) : '';
        return css `
      ${panelStyle}
      transform: ${transformStyle};
      border-radius: ${borderRadiusStyle};
      box-shadow: ${isAnimationPlayed ? boxShadowStyle : ''};
      max-height: 100vh;
      max-width: 100vw;
      ${getResizeStyle()}
    `;
    }, [LayoutsEntry, positionSizeJson, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.borderRadius, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow, isAnimationPlayed]);
    const getBannerStyle = React.useCallback((theme) => {
        var _a, _b, _c;
        const bgStyle = (cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.backgroundStyle) ? styleUtils.toBackgroundEmotionStyle(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.backgroundStyle) : '';
        const borderStyle = (cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.border) ? styleUtils.toCSSBorder(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.border) : null;
        const borderRadiusStyle = (cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.borderRadius) ? styleUtils.toCSSBorderRadius(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.borderRadius.asMutable({ deep: true })) : '2px';
        const boxShadowStyle = ((cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) && !((_a = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) === null || _a === void 0 ? void 0 : _a.presetStyle)) ? styleUtils.toCSSBoxshadow(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) : '0 0 10px 1px rgba(0,0,0,0.2)';
        const horizontalButtonsStyle = css `
      display: flex;
      flex-direction: ${bannerWidth > 640 ? 'row' : 'column'};
      justify-content: ${bannerWidth > 640 ? 'end' : 'center'};
      align-items: ${bannerWidth > 640 ? 'center' : ''}
    `;
        const verticalButtonsStyle = css `
      display: flex;
      flex-direction: ${bannerWidth > 480 ? 'row' : 'column'};
      justify-content: end;
    `;
        return css `
      overflow: auto;
      background: ${(_b = theme === null || theme === void 0 ? void 0 : theme.ref.palette) === null || _b === void 0 ? void 0 : _b.white};

      ${bgStyle}
      border: ${borderStyle !== null && borderStyle !== void 0 ? borderStyle : `${(_c = theme === null || theme === void 0 ? void 0 : theme.sys.color) === null || _c === void 0 ? void 0 : _c.secondary.light} solid 1px`};
      border-radius: ${borderRadiusStyle};
      box-shadow: ${boxShadowStyle};
      display: flex;
      flex-direction: ${(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.layout) === ECookieBannerLayout.Horizontal ? 'row' : 'column'};
      .banner-text-edit-button {
        position: absolute;
        top: -1rem;
        left: 1rem;
      }
      .cookie-banner-text {
        flex: 1 1 auto;
        overflow: auto;
      }
      .cookie-banner-text.dragging {
        user-select: none;
        pointer-events: none;
      }
      .banner-buttons {
        flex: 0 0 auto;
        ${(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.layout) === ECookieBannerLayout.Horizontal ? horizontalButtonsStyle : verticalButtonsStyle}
        button {
          max-height: 30px;
        }
      }
      .cookie-banner-text:hover, .cookie-banner-text.active {
        border-radius: 2px;
        border: ${window.jimuConfig.isInBuilder && appMode === AppMode.Design ? '1px solid var(--sys-color-primary-main)' : ''};
      }
      .ql-container {
        .ql-editor{
          overflow: hidden;
        }
      }
    `;
    }, [appMode, bannerWidth, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.backgroundStyle, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.border, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.borderRadius, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.layout]);
    const getModuleByPath = (modulePath) => __awaiter(this, void 0, void 0, function* () {
        if (!moduleLoader.getModuleSync(modulePath)) {
            yield window.mockimport(modulePath);
        }
        return moduleLoader.getModuleSync(modulePath);
    });
    React.useEffect(() => {
        setIsEditable(appMode === AppMode.Design);
        if (appMode === AppMode.Run || appMode === AppMode.Express) {
            setPlayId(getNextAnimationId());
        }
    }, [appMode]);
    React.useEffect(() => {
        if (previewDialogId === 'cookie-banner') {
            setIsAnimationPlayed(false);
            setPlayId(getNextAnimationId());
        }
    }, [previewDialogId]);
    hooks.useEffectWithPreviousValues((prevValues) => {
        if (!window.jimuConfig.isInBuilder) {
            return;
        }
        const [prevIsBannerOpen] = prevValues;
        if (prevIsBannerOpen !== isBannerOpen && isBannerOpen) {
            openOverlay();
        }
    }, [isBannerOpen, openOverlay]);
    React.useEffect(() => {
        function helper() {
            return __awaiter(this, void 0, void 0, function* () {
                const richTextEditorEntry = window.jimuConfig.isInBuilder ? yield getModuleByPath('jimu-ui/advanced/rich-text-editor') : null;
                const dndEntry = window.jimuConfig.isInBuilder ? yield getModuleByPath('jimu-core/dnd') : null;
                const themeEntry = yield getModuleByPath('jimu-theme');
                const builderEntry = window.jimuConfig.isInBuilder ? yield getModuleByPath('jimu-for-builder') : null;
                const layoutsEntry = yield getModuleByPath('jimu-layouts/layout-runtime');
                if (richTextEditorEntry) {
                    setRichEditorEntry(richTextEditorEntry);
                }
                if (themeEntry) {
                    setThemeEntry(themeEntry);
                }
                if (builderEntry) {
                    setBuilderEntry(builderEntry);
                }
                if (layoutsEntry) {
                    setLayoutsEntry(layoutsEntry);
                }
                if (dndEntry) {
                    setDndEntry(dndEntry);
                }
            });
        }
        helper();
    }, []);
    React.useEffect(() => {
        function calculateBannerStatus() {
            var _a;
            if (!cookieBannerConfig) {
                return;
            }
            // Hide the banner in the builder by default, wait for the privacy panel opening it
            if (window.jimuConfig.isInBuilder) {
                setIsBannerOpen(isCookieBannerOpenByPrivacyPanel || isCookieBannerOpenByUrl);
                return;
            }
            const DEFAULT_REFRESH_DAYS = 180;
            const COOKIE_CONFIRMATIONS_KEY = `${utils.getLocalStorageAppKey()}-cookie-confirmations`;
            if (utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY)) {
                let refreshPeriod = DEFAULT_REFRESH_DAYS;
                const currentTimeStamp = new Date().getTime();
                const cookieConfirmations = JSON.parse(utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY));
                const localUid = cookieConfirmations.uid;
                const confirmTime = cookieConfirmations.confirmTime;
                const renewTime = cookieBannerConfig.renewTime;
                const publishTime = (_a = getAppStore().getState().appConfig) === null || _a === void 0 ? void 0 : _a.publishTimestamp;
                const passDays = calculateDaysBetween(confirmTime || currentTimeStamp, currentTimeStamp);
                if (cookieBannerConfig.refreshPeriodically && typeof cookieBannerConfig.refreshPeriod === 'number') {
                    refreshPeriod = cookieBannerConfig.refreshPeriod;
                }
                if (passDays > refreshPeriod || localUid !== cookieBannerConfig.uid || isCookieBannerOpenByUrl || confirmTime < renewTime || (renewTime < confirmTime && confirmTime < publishTime)) {
                    setIsBannerOpen(true);
                }
            }
            else {
                // No local storage, show banner
                setIsBannerOpen(true);
            }
        }
        calculateBannerStatus();
    }, [cookieBannerConfig, isCookieBannerOpenByPrivacyPanel, isCookieBannerOpenByUrl]);
    hooks.useEffectWithPreviousValues((prevValues) => {
        const [prevInteractive] = prevValues;
        // For updates not caused by the change of the interactive, bind the handler
        if ((isEditable || appMode === AppMode.Express) && BuilderEntry && DndEntry && isBannerOpen && prevInteractive === interactive) {
            const debounceFunc = lodash.debounce(() => {
                if (interactive) {
                    interactive.unset();
                }
                if (bannerContainerRef.current) {
                    setInteractive(bindResizeHandler(DndEntry.interact, bannerContainerRef.current, editorRef.current, positionSizeJson, browserSizeMode, BuilderEntry.getAppConfigAction, (value) => { setIsEditable(value); }));
                }
            }, bannerContainerRef.current ? 0 : 1000);
            debounceFunc();
        }
    }, [interactive, BuilderEntry, DndEntry, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.positionSize, isEditable, isBannerOpen, positionSizeJson, bannerContainerRef, browserSizeMode]);
    // Duplicate because the direct import from builder widget will cause error
    const updateAppCookieBannerConfig = React.useCallback(function (propPath, value) {
        var _a, _b;
        if (!BuilderEntry) {
            console.error('Please load jimu-for-builder module first');
        }
        let appConfig = BuilderEntry.getAppConfigAction().appConfig;
        if (!appConfig.cookieBanner) {
            appConfig = appConfig.set('cookieBanner', {});
        }
        let cookieBanner = null;
        if (typeof propPath === 'string') {
            cookieBanner = (_a = appConfig.cookieBanner) === null || _a === void 0 ? void 0 : _a.set(propPath, value);
        }
        else {
            cookieBanner = (_b = appConfig.cookieBanner) === null || _b === void 0 ? void 0 : _b.setIn(propPath, value);
        }
        BuilderEntry.getAppConfigAction().updateCookieBannerConfig(cookieBanner).exec();
    }, [BuilderEntry]);
    const plugin = React.useCallback(() => {
        if (!RichEditorEntry || !ThemeEntry || !isEditingText) {
            return null;
        }
        return ({ editor, selection, formats }) => {
            return (jsx(ThemeEntry.ThemeSwitchComponent, { useTheme2: true },
                jsx(RichEditorEntry.Bubble, { editor: editor, selection: selection, formats: formats, placement: 'top', style: { minWidth: '428px' }, showLinkSetting: true, onChange: (text) => {
                        updateAppCookieBannerConfig('displayRichText', text);
                    } })));
        };
    }, [RichEditorEntry, ThemeEntry, isEditingText, updateAppCookieBannerConfig]);
    const hideBannerAndUrlParam = React.useCallback(() => {
        setIsBannerOpen(false);
        getAppStore().dispatch(appActions.OpenCookieBannerByUrl(false));
        jimuHistory.changeQueryObject({ cookie_banner: null }, true);
    }, []);
    const onConfirmFactory = React.useCallback((accept) => {
        return () => {
            if (isEditable) {
                // Do not respond click event in design mode
                return;
            }
            const COOKIE_CONFIRMATIONS_KEY = `${utils.getLocalStorageAppKey()}-cookie-confirmations`;
            const prevConfirmationsStr = utils.readLocalStorage(COOKIE_CONFIRMATIONS_KEY);
            let cookieConfirmations = null;
            if (prevConfirmationsStr) {
                cookieConfirmations = JSON.parse(prevConfirmationsStr);
            }
            cookieConfirmations = {
                uid: cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.uid,
                confirmTime: new Date().getTime(),
                performance: accept
            };
            // Do not save to the local storage if it is live-view
            if (!window.jimuConfig.isInBuilder) {
                utils.setLocalStorage(COOKIE_CONFIRMATIONS_KEY, JSON.stringify(cookieConfirmations));
            }
            else {
                BuilderEntry && BuilderEntry.appBuilderSync.publishCloseBannerToBuilder();
            }
            hideBannerAndUrlParam();
        };
    }, [BuilderEntry, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.uid, hideBannerAndUrlParam, isEditable]);
    const onCloseClicked = React.useCallback(() => {
        if (isEditable) {
            return;
        }
        const COOKIE_CONFIRMATIONS_KEY = `${utils.getLocalStorageAppKey()}-cookie-confirmations`;
        const cookieConfirmations = {
            uid: cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.uid,
            confirmTime: new Date().getTime()
        };
        // Do not save to the local storage if it is live-view
        if (!window.jimuConfig.isInBuilder) {
            utils.setLocalStorage(COOKIE_CONFIRMATIONS_KEY, JSON.stringify(cookieConfirmations));
        }
        else {
            BuilderEntry && BuilderEntry.appBuilderSync.publishCloseBannerToBuilder();
        }
        hideBannerAndUrlParam();
    }, [BuilderEntry, cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.uid, hideBannerAndUrlParam, isEditable]);
    const onPreferencesClicked = React.useCallback(() => {
        if (isEditable) {
            return;
        }
        setOpenSettingsWindow(true);
    }, [isEditable]);
    const onSettingsWindowSave = React.useCallback(() => {
        if (isEditable) {
            return;
        }
        hideBannerAndUrlParam();
    }, [hideBannerAndUrlParam, isEditable]);
    const onWindowResize = lodash.debounce(React.useCallback(({ width, height }) => {
        // Get the latest banner width
        setBannerWidth(width);
    }, []), 300);
    if (!isBannerOpen || !(cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.enable)) {
        return null;
    }
    return (jsx(ModalOverlayIdContext.Provider, { value: overlayId },
        jsx(OverlayPortal, null,
            jsx("div", { className: 'cookie-banner-wrapper', css: getWrapperStyle(zIndex) },
                window.jimuConfig.isInBuilder && jsx("div", { className: 'cookie-banner-mask', css: getMaskStyle() }),
                jsx("div", { className: 'cookie-banner-container', css: getContainerStyle(), ref: (el) => { bannerContainerRef.current = el; } },
                    jsx(ReactResizeDetector, { handleHeight: true, handleWidth: true, targetRef: bannerContainerRef.current, onResize: onWindowResize }),
                    jsx(AnimationComponent, { className: 'h-100', playId: playId, parentId: null, type: (_f = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.effect) === null || _f === void 0 ? void 0 : _f.type, configType: (_h = (_g = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.effect) === null || _g === void 0 ? void 0 : _g.option) === null || _h === void 0 ? void 0 : _h.configType, direction: (_k = (_j = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.effect) === null || _j === void 0 ? void 0 : _j.option) === null || _k === void 0 ? void 0 : _k.direction, onAnimationEnd: () => {
                            setIsAnimationPlayed(true);
                            getAppStore().dispatch(appActions.clearAnimationPreview());
                        } },
                        jsx("div", { className: `cookie-banner h-100 ${(_m = (_l = cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.boxShadow) === null || _l === void 0 ? void 0 : _l.presetStyle) !== null && _m !== void 0 ? _m : ''}`, css: getBannerStyle(theme), onClick: () => { setIsEditingText(true); } },
                            (isEditable || appMode === AppMode.Express) && jsx(BannerResizeHandler, { positionSizeJson: positionSizeJson }),
                            jsx("div", { className: 'cookie-banner-text m-2 p-2 flex-grow-1', ref: editorRef }, (RichEditorEntry && (isEditable || appMode === AppMode.Express))
                                ? jsx(RichEditorEntry.RichTextEditor, { plugin: plugin(), value: displayText, onEditorBlur: (selection, source, editor, ...args) => {
                                        const html = editor.getHTML();
                                        updateAppCookieBannerConfig('displayRichText', html);
                                        // Only operate the classList here since onBlur will be triggered even when you click the toolbar
                                        editorRef.current && editorRef.current.classList.remove('active');
                                    }, onBlur: (event) => {
                                        const relatedTarget = event.relatedTarget;
                                        let currNode = relatedTarget;
                                        let isFormatPanelClicked = false;
                                        while (currNode) {
                                            if (currNode.classList && currNode.classList.contains('format-panel')) {
                                                isFormatPanelClicked = true;
                                                break;
                                            }
                                            currNode = currNode.parentNode;
                                        }
                                        if (!isFormatPanelClicked) {
                                            setIsEditingText(false);
                                        }
                                    }, onEditorFocus: (selection, source, editor) => {
                                        editorRef.current && editorRef.current.classList.add('active');
                                    } })
                                : jsx(RichTextDisplayer, { value: displayText })),
                            jsx("div", { className: 'banner-buttons' },
                                enableTracking &&
                                    jsx(Button, { type: 'primary', className: 'm-2 text-nowrap', onClick: onConfirmFactory(true) },
                                        (acceptAllButtonConfig === null || acceptAllButtonConfig === void 0 ? void 0 : acceptAllButtonConfig.icon) && jsx(Icon, { icon: acceptAllButtonConfig.icon.svg }),
                                        (acceptAllButtonConfig === null || acceptAllButtonConfig === void 0 ? void 0 : acceptAllButtonConfig.label) || translate('acceptAll')),
                                (enableTracking && (declineAllButtonConfig === null || declineAllButtonConfig === void 0 ? void 0 : declineAllButtonConfig.enable)) &&
                                    jsx(Button, { type: 'secondary', className: 'm-2 text-nowrap', onClick: onConfirmFactory(false) },
                                        (declineAllButtonConfig === null || declineAllButtonConfig === void 0 ? void 0 : declineAllButtonConfig.icon) && jsx(Icon, { icon: declineAllButtonConfig.icon.svg }),
                                        (declineAllButtonConfig === null || declineAllButtonConfig === void 0 ? void 0 : declineAllButtonConfig.label) || translate('declineAll')),
                                !enableTracking && jsx(Button, { className: 'm-2 text-nowrap', onClick: onCloseClicked },
                                    (closeButtonConfig === null || closeButtonConfig === void 0 ? void 0 : closeButtonConfig.icon) && jsx(Icon, { icon: closeButtonConfig.icon.svg }),
                                    (closeButtonConfig === null || closeButtonConfig === void 0 ? void 0 : closeButtonConfig.label) || translate('close')),
                                (enableTracking && (cookieSettingsButtonConfig === null || cookieSettingsButtonConfig === void 0 ? void 0 : cookieSettingsButtonConfig.enable)) &&
                                    jsx(Button, { className: 'm-2 text-nowrap', onClick: onPreferencesClicked },
                                        (cookieSettingsButtonConfig === null || cookieSettingsButtonConfig === void 0 ? void 0 : cookieSettingsButtonConfig.icon) && jsx(Icon, { icon: cookieSettingsButtonConfig.icon.svg }),
                                        (cookieSettingsButtonConfig === null || cookieSettingsButtonConfig === void 0 ? void 0 : cookieSettingsButtonConfig.label) || translate('preferences'))),
                            openSettingsWindow && jsx(CookieSettingsWindow, { isEditable: isEditable, builderEntry: BuilderEntry, bannerUid: cookieBannerConfig === null || cookieBannerConfig === void 0 ? void 0 : cookieBannerConfig.uid, toggle: () => { setOpenSettingsWindow(false); }, onSave: onSettingsWindowSave }))))))));
};
function BannerResizeHandler(props) {
    var _a, _b, _c;
    const { positionSizeJson } = props;
    return (jsx("div", { className: 'cookie-banner-resize-handlers' },
        (!positionSizeJson.position.includes('TOP') || positionSizeJson.heightMode === 'STRETCH') &&
            jsx("span", { key: '1', className: 'resize-handler cookie-banner-resize-top' },
                jsx("div", { className: 'resize-handler-icon h-100' })),
        (!((_a = positionSizeJson === null || positionSizeJson === void 0 ? void 0 : positionSizeJson.position) === null || _a === void 0 ? void 0 : _a.includes('BOTTOM')) || positionSizeJson.heightMode === 'STRETCH') &&
            jsx("span", { key: '2', className: 'resize-handler cookie-banner-resize-bottom' },
                jsx("div", { className: 'resize-handler-icon h-100' })),
        (!((_b = positionSizeJson === null || positionSizeJson === void 0 ? void 0 : positionSizeJson.position) === null || _b === void 0 ? void 0 : _b.includes('LEFT')) || positionSizeJson.widthMode === 'STRETCH') &&
            jsx("span", { key: '3', className: 'resize-handler cookie-banner-resize-left' },
                jsx("div", { className: 'resize-handler-icon w-100' })),
        (!((_c = positionSizeJson === null || positionSizeJson === void 0 ? void 0 : positionSizeJson.position) === null || _c === void 0 ? void 0 : _c.includes('RIGHT')) || positionSizeJson.widthMode === 'STRETCH') &&
            jsx("span", { key: '4', className: 'resize-handler cookie-banner-resize-right' },
                jsx("div", { className: 'resize-handler-icon w-100' }))));
}
